import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLanguage } from '@/contexts/LanguageContext';
import feedbackService from '@/services/feedbackService';
import './Message.css';

const Message = ({ message, isUser, onClickableTextClick, sessionId, userId, token }) => {
  const { t, language } = useLanguage();
  const [feedbackState, setFeedbackState] = useState(null); // 'like', 'dislike', or null
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  // Check if message contains a table or is long (>400 chars)
  const hasTable = !isUser && message.text && (
    message.text.includes('<table') || 
    message.text.includes('|') && message.text.includes('---')
  );
  
  const isLongMessage = !isUser && message.text && message.text.length > 400;
  
  const shouldBeWide = hasTable || isLongMessage;

  // Function to parse and replace <click> tags with clickable span elements
  const parseClickableTags = (text) => {
    // Case-insensitive regex to match <click>content</click>
    const clickRegex = /<click>(.*?)<\/click>/gi;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = clickRegex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.substring(lastIndex, match.index)
        });
      }
      
      // Add the clickable part
      parts.push({
        type: 'clickable',
        content: match[1],
        id: `click-${Date.now()}-${Math.random()}`
      });
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex)
      });
    }
    
    return parts.length > 0 ? parts : [{ type: 'text', content: text }];
  };

  const handleClickableClick = (text) => {
    if (onClickableTextClick && typeof onClickableTextClick === 'function') {
      onClickableTextClick(text);
    }
  };

  const handleFeedback = async (type) => {
    // If already submitting, don't allow another click
    if (isSubmittingFeedback) return;

    // Toggle feedback: if clicking same type, remove it; otherwise set new type
    const newState = feedbackState === type ? null : type;
    setFeedbackState(newState);

    // Only submit to API if setting a new feedback (not removing)
    if (newState !== null) {
      setIsSubmittingFeedback(true);
      
      try {
        const result = await feedbackService.submitFeedback({
          sessionId: sessionId,
          messageText: message.text,
          feedbackAction: type,
          clientUserId: userId,
          token: token,
        });

        if (result.success) {
          console.log(`✅ Feedback "${type}" submitted successfully`);
        } else {
          console.error('❌ Failed to submit feedback:', result.error);
          // Optionally revert the feedback state on error
          // setFeedbackState(feedbackState);
        }
      } catch (error) {
        console.error('❌ Error submitting feedback:', error);
      } finally {
        setIsSubmittingFeedback(false);
      }
    }
  };

  return (
    <div className={`message ${isUser ? 'message-user' : 'message-bot'}`}>
      <div className={`message-wrapper ${shouldBeWide ? 'message-wrapper-wide' : ''}`}>
        <div className="message-header">
          {!isUser && (
            <div className="message-avatar">
              <img src="/assets/agent-logo-v2.svg" alt="Agent" className="agent-avatar-icon" />
            </div>
          )}
          <div className="message-time">
            <span className="message-label">{isUser ? t('visitor') : t('livechat')}</span>
            <span className="message-timestamp">
              {new Date(message.timestamp).toLocaleTimeString(language === 'ar' ? 'ar-SA' : 'en-US', { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </span>
          </div>
        </div>
        <div className="message-content">
          <div className="message-text">
            {isUser ? (
              message.text
            ) : (
              <>
                {parseClickableTags(message.text).map((part, index) => {
                  if (part.type === 'clickable') {
                    return (
                      <span
                        key={part.id || index}
                        className="clickable-text"
                        onClick={() => handleClickableClick(part.content)}
                        role="button"
                        tabIndex={0}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handleClickableClick(part.content);
                          }
                        }}
                      >
                        {part.content}
                      </span>
                    );
                  } else {
                    return (
                      <ReactMarkdown 
                        key={index}
                        remarkPlugins={[remarkGfm]}
                        components={{
                          // Custom rendering for better Arabic RTL support
                          p: ({node, ...props}) => <p dir="auto" {...props} />,
                          ol: ({node, ...props}) => <ol dir="auto" {...props} />,
                          ul: ({node, ...props}) => <ul dir="auto" {...props} />,
                          li: ({node, ...props}) => <li dir="auto" {...props} />,
                          td: ({node, ...props}) => <td dir="auto" {...props} />,
                          th: ({node, ...props}) => <th dir="auto" {...props} />,
                          // Open links in a new window
                          a: ({node, ...props}) => <a target="_blank" rel="noopener noreferrer" {...props} />,
                        }}
                      >
                        {part.content}
                      </ReactMarkdown>
                    );
                  }
                })}
              </>
            )}
          </div>
          {!isUser && (
            <div className="message-feedback">
              <button
                className={`feedback-btn ${feedbackState === 'like' ? 'active' : ''}`}
                onClick={() => handleFeedback('like')}
                aria-label="Like"
                title="Like"
              >
                <img src="/assets/btn-like.svg" alt="Like" />
              </button>
              <button
                className={`feedback-btn ${feedbackState === 'dislike' ? 'active' : ''}`}
                onClick={() => handleFeedback('dislike')}
                aria-label="Dislike"
                title="Dislike"
              >
                <img src="/assets/btn-dislike.svg" alt="Dislike" />
              </button>
            </div>
          )}
        </div>
        {isUser && message.status === 'sent' && (
          <div className="message-read-status">{t('read')}</div>
        )}
      </div>
    </div>
  );
};

export default Message;

